'use client'

import { useUpFetch } from '@repo/sdk/upfetch'
import { Spinner } from '@repo/ui/components'
import { cn } from '@repo/ui/lib'
import { useCallback, useEffect, useState } from 'react'

const useInquiryDocDownloadUrl = () => {
  const upFetch = useUpFetch()
  const getDownloadUrl = useCallback(
    async (loadId: number, documentId: number) => {
      const res = await upFetch<{ url: string }>(
        `/loads/${loadId}/documents/${documentId}/download`,
      )
      return res.url
    },
    [upFetch],
  )

  return { getDownloadUrl }
}

export const DocumentsList = ({
  className,
  documents,
  loadId,
}: {
  className?: string
  documents: any[]
  loadId: number
}) => {
  const [downloadables, setDownloadables] = useState<any[]>([])
  const { getDownloadUrl } = useInquiryDocDownloadUrl()

  useEffect(() => {
    const fetchDocumentUrls = async () => {
      if (!documents || documents.length === 0 || !loadId) {
        setDownloadables([])
        return
      }

      const urls = await Promise.all(
        documents.map(async doc => {
          try {
            const url = await getDownloadUrl(loadId, doc.id)
            return { ...doc, media: { ...doc.media, url } }
          } catch (error) {
            console.error('Error fetching download URL:', error)
            return { ...doc, media: { ...doc.media, url: '' } }
          }
        }),
      )
      setDownloadables(urls)
    }

    if (documents) {
      fetchDocumentUrls()
    }
  }, [loadId, documents, getDownloadUrl]) // getDownloadUrl is now memoized

  if (!downloadables.length) return <></>

  return (
    <div className={cn('dark:text-foreground flex flex-col gap-4 text-left', className)}>
      <table>
        <thead>
          <tr className="border-b border-neutral-200 py-2 text-xs text-[12px] font-normal text-[#959595]">
            <th className="w-1/4">Shipment file</th>
            <th className="max-w-12">Document type</th>
            <th className="w-1/4">Download</th>
          </tr>
        </thead>
        <tbody>
          {downloadables.map((document, index) => (
            <tr key={document.id || index}>
              <td className="max-w-48 py-2 pr-5 text-sm text-wrap">{document.media.name}</td>
              <td className={'max-w-12 text-xs'}>{document.type}</td>
              <td className={'text-xs'}>
                {document.media.url ? (
                  <a
                    className="hover:text-brand text-sm transition-colors"
                    href={document.media.url}
                    target={'_blank'}
                  >
                    Download
                  </a>
                ) : (
                  <span>
                    <Spinner />
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
